import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ListChecks, CheckCircle2, Bug, Coins, ArrowRight, Sparkles, ArrowUpRight,
  RefreshCw, WifiOff, Loader2, Download, Zap, AlertTriangle, TriangleAlert, Bot,
  Activity, Gauge, Timer, FolderKanban,
} from "lucide-react";
import Aurora from "@/components/common/Aurora";
import StatCard from "@/components/dashboard/StatCard";
import CountUp from "@/components/dashboard/CountUp";
import { GroupedBars, TrendBars } from "@/components/dashboard/Charts";
import { StatusBadge } from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { toastError } from "@/lib/toast";
import { useApiData } from "@/hooks/useApiData";
import { useRefreshAll } from "@/hooks/useRefreshAll";
import { useAuth } from "@/context/AuthContext";
import { getUsage } from "@/api/billing";
import { listTestRuns, listBugs } from "@/api/runs";
import { listExtRuns, downloadExtension } from "@/api/extension";
import { downloadAgent } from "@/api/exe";
import { listExeRuns } from "@/api/exe";
import { FLOWS, FLOW_BY_KEY, normalizeRun, flowStats, perDaySeries, pickActivityWindow, isBug } from "@/lib/flows";

// ---------------------------------------------------------------------------
// Tester dashboard — reads the tester's OWN activity (mine=true on every list)
// across the three testing ways: Dashboard (cloud), Extension, and Exe Agent.
// ---------------------------------------------------------------------------
const now = Date.now();
const iso = (h) => new Date(now - h * 3600000).toISOString();

const DEMO_DASH = [
  { id: "d1", status: "passed", url: "https://acme.com/login", instruction: "Login happy path", created_at: iso(2), duration_seconds: 12, steps_passed: 5, steps_failed: 0 },
  { id: "d2", status: "failed", url: "https://acme.com/cart", instruction: "Checkout with an empty cart", created_at: iso(6), duration_seconds: 8, steps_passed: 2, steps_failed: 1 },
  { id: "d3", status: "passed", url: "https://acme.com/search", instruction: "Search returns results", created_at: iso(26), duration_seconds: 10, steps_passed: 4, steps_failed: 0 },
];
const DEMO_EXT = [
  { id: "e1", status: "passed", url: "https://app.acme.com", instruction: "Filter the reports table", browser: "chromium", created_at: iso(3), duration_seconds: 9, token_usage: { estimated_cost_usd: 0.02 } },
  { id: "e2", status: "failed", url: "https://app.acme.com/settings", instruction: "Update the billing email", browser: "chromium", created_at: iso(9), duration_seconds: 14, token_usage: { estimated_cost_usd: 0.03 } },
];
const DEMO_EXE = [
  { id: "x1", status: "passed", url: "https://internal.acme.com", instruction: "Smoke test the internal portal", browser: "chromium", created_at: iso(1), duration_seconds: 33, token_usage: { estimated_cost_usd: 0.05 } },
  { id: "x2", status: "error", url: "https://internal.acme.com/orders", instruction: "Create an order end-to-end", browser: "edge", created_at: iso(20), duration_seconds: 47, token_usage: { estimated_cost_usd: 0.08 } },
];
const DEMO_BUGS = [
  { id: "d2", bug: { title: "Checkout allows empty cart", severity: "high" }, url: "https://acme.com/cart", resolved: false },
];

function relTime(v) {
  try {
    const m = Math.floor((Date.now() - new Date(v).getTime()) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
  } catch (e) { return "—"; }
}
function fmtDur(s) { return !s ? "—" : s < 60 ? `${Math.round(s)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`; }

// Sum a per-day metric (cost) over the last `days`, aligned with perDaySeries.
function perDayCost(runs, days = 7) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const buckets = [];
  for (let i = days - 1; i >= 0; i--) { const d = new Date(today); d.setDate(today.getDate() - i); buckets.push({ key: d.getTime(), v: 0 }); }
  const idx = new Map(buckets.map((b) => [b.key, b]));
  runs.forEach((r) => {
    const d = new Date(r.createdAt); if (Number.isNaN(d.getTime())) return;
    d.setHours(0, 0, 0, 0); const b = idx.get(d.getTime()); if (b) b.v += r.cost || 0;
  });
  return buckets.map((b) => Number(b.v.toFixed(3)));
}

function Section({ children, delay = 0, className }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
}

const CHIP_TONE = {
  brand: "bg-brand-500/10 text-brand-700 dark:text-brand-200",
  emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-200",
  rose: "bg-rose-500/10 text-rose-700 dark:text-rose-200",
};
function StatChip({ icon: Icon, label, value, tone = "brand", delay = 0 }) {
  return (
    <motion.span
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35, ease: "easeOut" }}
      className="inline-flex items-center gap-2 rounded-full border border-slate-200/70 bg-white/70 px-3 py-1.5 text-sm shadow-soft backdrop-blur dark:border-white/10 dark:bg-white/5"
    >
      <span className={cn("grid h-6 w-6 place-items-center rounded-full", CHIP_TONE[tone] || CHIP_TONE.brand)}>
        <Icon size={13} />
      </span>
      <span className="font-semibold text-slate-900 dark:text-white">{value}</span>
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
    </motion.span>
  );
}

function RunRowSkeleton() {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="h-8 w-8 shrink-0 animate-pulse rounded-lg bg-slate-200 dark:bg-white/10" />
      <div className="h-3.5 flex-1 animate-pulse rounded bg-slate-200 dark:bg-white/10" />
      <div className="h-5 w-16 shrink-0 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
    </div>
  );
}

export default function TesterHome() {
  const { user } = useAuth();

  // The three flows — the tester's OWN runs (mine=true).
  const dash = useApiData(() => listTestRuns({ mine: true, limit: 200 }), DEMO_DASH);
  const ext = useApiData(() => listExtRuns(200, { mine: true }), DEMO_EXT);
  const exe = useApiData(() => listExeRuns(200, { mine: true }), DEMO_EXE);
  const bugsState = useApiData(() => listBugs({ mine: true, limit: 200 }), DEMO_BUGS);

  const [usage, setUsage] = useState(null);
  const [extBusy, setExtBusy] = useState(false);
  const [exeBusy, setExeBusy] = useState(false);
  const [extMsg, setExtMsg] = useState(null);
  // Owned so it can be cleared: both download handlers used to call setTimeout
  // with no cleanup, which set state on an unmounted tree if you navigated away
  // inside the five seconds (and let two downloads race each other's banner).
  const extMsgTimer = useRef(null);
  const flashMsg = useCallback((msg) => {
    setExtMsg(msg);
    window.clearTimeout(extMsgTimer.current);
    extMsgTimer.current = window.setTimeout(() => setExtMsg(null), 5000);
  }, []);
  useEffect(() => () => window.clearTimeout(extMsgTimer.current), []);

  // A failed usage call used to be swallowed here (`.catch(() => {})`), so every
  // headline card silently fell back to the client-side tally over the capped run
  // lists — a truncated number under the label "across all flows", with nothing
  // on screen saying so. The refresh path already reported its failures.
  useEffect(() => {
    getUsage()
      .then(setUsage)
      .catch((e) => toastError(e?.message || "Couldn't load your plan usage — totals may be incomplete."));
  }, []);

  const loading = dash.loading || ext.loading || exe.loading || bugsState.loading;
  const demo = dash.demo || ext.demo || exe.demo || bugsState.demo;

  const runsByFlow = useMemo(() => ({
    dashboard: (Array.isArray(dash.data) ? dash.data : []).map((r) => normalizeRun(r, "dashboard")),
    extension: (Array.isArray(ext.data) ? ext.data : []).map((r) => normalizeRun(r, "extension")),
    exe: (Array.isArray(exe.data) ? exe.data : []).map((r) => normalizeRun(r, "exe")),
  }), [dash.data, ext.data, exe.data]);

  const allRuns = useMemo(
    () => [...runsByFlow.dashboard, ...runsByFlow.extension, ...runsByFlow.exe],
    [runsByFlow]
  );

  // Every list above is capped at 200 rows per flow, so counting them gave a
  // headline that stopped growing at 600 while still being labelled "Total runs
  // · across all flows" — and a pass rate computed from that same truncated
  // window. `runs_by_flow` is counted in SQL over EVERY run this tester owns, so
  // the cards below are exact. The client-side tally stays as the fallback for
  // demo mode and for a server too old to send the field.
  const serverFlows = usage && usage.runs_by_flow && typeof usage.runs_by_flow === "object"
    ? usage.runs_by_flow
    : null;
  const statsFor = useCallback((flowKey, runs) => {
    const local = flowStats(runs);
    const srv = serverFlows && serverFlows[flowKey];
    if (!srv || typeof srv.total !== "number") return local;
    return {
      ...local,
      total: srv.total,
      passed: srv.passed || 0,
      failed: srv.failed || 0,
      passRate: srv.total ? Math.round(((srv.passed || 0) / srv.total) * 100) : 0,
    };
  }, [serverFlows]);

  const stats = useMemo(() => {
    const local = flowStats(allRuns);
    if (!serverFlows) return local;
    const total = FLOWS.reduce((n, f) => n + ((serverFlows[f.key] || {}).total || 0), 0);
    const passed = FLOWS.reduce((n, f) => n + ((serverFlows[f.key] || {}).passed || 0), 0);
    const failed = FLOWS.reduce((n, f) => n + ((serverFlows[f.key] || {}).failed || 0), 0);
    return { ...local, total, passed, failed, passRate: total ? Math.round((passed / total) * 100) : 0 };
  }, [allRuns, serverFlows]);
  // One window for every chart here, chosen from ALL runs — see pickActivityWindow.
  // Declared before its consumers: `const` is in the temporal dead zone until this
  // line runs, so reading it above would throw rather than read undefined.
  const activityDays = useMemo(() => pickActivityWindow(allRuns), [allRuns]);
  const combinedSpark = useMemo(() => perDaySeries(allRuns, activityDays).data, [allRuns, activityDays]);
  const spendData = useMemo(() => perDayCost(allRuns, activityDays), [allRuns, activityDays]);
  const runsToday = combinedSpark[combinedSpark.length - 1] || 0;
  // Lifetime spend comes from the SERVER, which sums every run this tester has ever
  // made — single, suite and batch, in all three runners. Adding it up here instead
  // only ever saw the newest 200 runs per flow, so a tester with months of live
  // testing was shown a fraction of their real bill. The client-side sum stays as
  // the fallback for demo mode and for a server too old to send the field.
  const fetchedSpend = useMemo(
    () => allRuns.reduce((s, r) => s + (r.cost || 0), 0), [allRuns]);
  const spend = usage && typeof usage.spend_total_usd === "number"
    ? usage.spend_total_usd
    : fetchedSpend;

  // Bugs: a BUG is a run that produced a bug report, NOT merely one that failed.
  // These counted failures, so "Open bugs" was really "failures" and disagreed
  // with the company Runs & Bugs page reading the very same runs. Cloud bugs come
  // from bug_reports (with a resolved-on-rerun flag); the headed runners carry
  // `has_bug` on the run row, and the server's exact count is preferred over the
  // capped list for the same reason the run totals are.
  const dashBugs = Array.isArray(bugsState.data) ? bugsState.data : [];
  const openDashBugs = dashBugs.filter((b) => !b.resolved).length;
  // `bugs_open` excludes a bug a later passing run of the same case already
  // cleared — the same rule the cloud flow applies with `resolved`. Reading
  // `bugs` here counted every bug the headed runners ever raised, so a card
  // labelled "Open bugs · need triage" added resolved ones to unresolved ones.
  // `bugs` stays as the fallback for a server too old to send the open count.
  const bugCount = (flowKey, runs) => {
    const srv = serverFlows && serverFlows[flowKey];
    if (srv && typeof srv.bugs_open === "number") return srv.bugs_open;
    if (srv && typeof srv.bugs === "number") return srv.bugs;
    return runs.filter(isBug).length;
  };
  const extBugs = bugCount("extension", runsByFlow.extension);
  const exeBugs = bugCount("exe", runsByFlow.exe);
  const openBugs = openDashBugs + extBugs + exeBugs;
  // The dashboard row uses the SAME open-bug figure as the KPI above it. It used
  // to use `dashBugs.length`, which includes bugs already resolved by a later
  // passing re-run — two different numbers for one thing on one screen.
  const bugsByFlow = { dashboard: openDashBugs, extension: extBugs, exe: exeBugs };
  const maxBugs = Math.max(1, ...Object.values(bugsByFlow));

  const perFlow = useMemo(() => FLOWS.map((f) => {
    const runs = runsByFlow[f.key];
    return { ...f, stats: statsFor(f.key, runs), series: perDaySeries(runs, activityDays) };
  }), [runsByFlow, activityDays, statsFor]);

  const stackedSeries = perFlow.map((f) => ({ key: f.key, label: f.label, color: f.bar, data: f.series.data }));
  // Every flow shares one window, so any series carries the same axis — but read
  // it from the array's first entry rather than a hardcoded flow index.
  const activityLabels = perFlow[0]?.series.labels || [];
  const activityTitles = perFlow[0]?.series.titles || [];
  const windowNote = activityDays === 7
    ? "last 7 days"
    : `last ${activityDays} days · no runs in the last 7`;

  const recent = useMemo(
    () => [...allRuns].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6),
    [allRuns]
  );

  const atLimit = usage && usage.runs_remaining === 0;
  const nearLimit = usage && !atLimit && usage.runs_percent >= 80;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || user?.email?.split("@")[0] || "there").split(" ")[0];

  // The usage call is a plain fetch rather than a useApiData source, so it is
  // wrapped to resolve like one — returning the payload on success and letting
  // the hook count a rejection as a section that failed to refresh. It used to
  // end in `.catch(() => {})`, which discarded the failure entirely.
  const { refreshing, refresh: refreshAll } = useRefreshAll(
    [
      dash.reload, ext.reload, exe.reload, bugsState.reload,
      () => getUsage().then((u) => { setUsage(u); return u; }),
    ],
    { label: "Dashboard" },
  );

  // Both downloads report through the same banner: two independent "saved" /
  // "failed" states would let one silently overwrite the other's message.
  async function downloadExe() {
    setExeBusy(true); setExtMsg(null);
    try {
      await downloadAgent();
      flashMsg({ type: "ok", text: "Agent download started — unzip and run aiqa-agent.exe." });
    } catch (e) {
      flashMsg({ type: "err", text: e.message || "Download failed." });
    } finally {
      setExeBusy(false);
    }
  }

  async function downloadExt() {
    setExtBusy(true); setExtMsg(null);
    try {
      const r = await downloadExtension();
      flashMsg({ type: "ok", text: `Saved ${r.filename} (${r.sizeLabel})` });
    } catch (e) {
      flashMsg({ type: "err", text: e.message || "Download failed." });
    } finally {
      setExtBusy(false);
    }
  }

  // Per-flow highlight stat on each flow card.
  // Keyed lookup, not perFlow[1] / perFlow[2]: those indexes silently assumed the
  // order of the FLOWS array, so reordering or hiding a flow would have labelled
  // one card with another's average.
  const statsByFlow = Object.fromEntries(perFlow.map((f) => [f.key, f.stats]));
  const flowExtra = {
    dashboard: { icon: Bug, text: `${openDashBugs} open bug${openDashBugs === 1 ? "" : "s"}` },
    extension: { icon: Timer, text: `${fmtDur(statsByFlow.extension?.avgDur)} avg run` },
    exe: { icon: Timer, text: `${fmtDur(statsByFlow.exe?.avgDur)} avg run` },
  };
  // The "dashboard" (cloud) flow is paused, and Runs & Bugs refuses to select a
  // disabled flow — so linking it to a bare /dashboard/runs silently landed you
  // on the Extension tab under a card labelled "Tester Dashboard". Render that
  // card inert instead of sending people somewhere they didn't ask for.
  const flowRoute = (key) => `/dashboard/runs?flow=${key}`;
  const flowDisabled = (key) => key === "dashboard";

  return (
    <>
      {/* hero band */}
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900 sm:p-7"
      >
        <Aurora className="opacity-70" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-300">Dashboard</p>
            <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
              {greeting}, {firstName}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {user?.company_name ? `${user.company_name} · your testing across all three flows.` : "Your testing across all three flows."}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <StatChip icon={Activity} label="runs today" value={runsToday} tone="brand" delay={0.1} />
              <StatChip icon={CheckCircle2} label="pass rate" value={`${stats.passRate}%`} tone="emerald" delay={0.16} />
              <StatChip icon={Bug} label="open bugs" value={openBugs} tone="rose" delay={0.22} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshing}>
              <RefreshCw size={16} className={cn(refreshing && "animate-spin")} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
            <Button variant="outline" size="sm" onClick={downloadExt} disabled={extBusy}>
              {extBusy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Extension
            </Button>
            {/* The Exe agent was reachable only from its own tab, so a tester
                setting up their first machine had to know to go looking. */}
            <Button variant="outline" size="sm" onClick={downloadExe} disabled={exeBusy}
                    title="Download the desktop agent (.zip) — your admin issues the key">
              {exeBusy ? <Loader2 size={16} className="animate-spin" /> : <Bot size={16} />} Exe agent
            </Button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {extMsg && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className={cn(
              "mb-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
              extMsg.type === "ok"
                ? "border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200"
                : "border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
            )}
          >
            {extMsg.type === "ok" ? <CheckCircle2 size={15} /> : <TriangleAlert size={15} />} {extMsg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* usage alerts */}
      {atLimit && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="shrink-0" />
            {/* "your team", not "you". runs_this_month / runs_limit are counted
                across the whole COMPANY (see _runs_this_month), so telling one
                tester they personally exhausted the plan was wrong on any
                account with more than one seat. */}
            <span><strong>Run limit reached</strong> — your team has used all {usage.runs_limit} runs on the {String(usage.plan_id || "").replace(/_/g, " ")} plan this month.</span>
          </div>
          <Button to="/dashboard/billing" size="sm" className="shrink-0"><Zap size={14} /> Upgrade</Button>
        </div>
      )}
      {nearLimit && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <div className="flex items-center gap-2">
            <Zap size={16} className="shrink-0" />
            <span><strong>{usage.runs_remaining} runs remaining</strong> on your team&apos;s plan this month ({usage.runs_percent}% used).</span>
          </div>
          <Link to="/dashboard/billing" className="shrink-0 text-xs font-semibold text-amber-700 hover:underline dark:text-amber-300">View plan →</Link>
        </div>
      )}
      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      {/* Combined KPIs — across all three flows */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={ListChecks} label="Total runs" value={<CountUp to={stats.total} />} sub="across all flows" spark={combinedSpark} sparkTitles={activityTitles} />
        <StatCard icon={Gauge} label="Pass rate" value={<CountUp to={stats.passRate} suffix="%" />} accent="emerald" sub={`${stats.passed} passed`} delay={0.05} />
        <StatCard icon={Bug} label="Open bugs" value={<CountUp to={openBugs} />} accent="rose" sub="need triage" delay={0.1} />
        <StatCard
          icon={Coins}
          label="Spend"
          // A lifetime total runs to dollars, not fractions of a cent: three decimals
          // is right for a single cheap run and unreadable for a real bill.
          value={<CountUp to={spend} prefix="$" decimals={spend >= 10 ? 2 : 3} />}
          accent="amber"
          sub={usage && typeof usage.spend_this_month_usd === "number"
            ? `all flows · $${usage.spend_this_month_usd.toFixed(2)} this month`
            : "all flows"}
          delay={0.15}
          spark={spendData.some((n) => n > 0) ? spendData : undefined}
        />
      </div>

      {/* Flow cards — the signature row */}
      <div className="mt-4 grid gap-4 md:grid-cols-3">
        {perFlow.map((f, i) => {
          const Extra = flowExtra[f.key].icon;
          const off = flowDisabled(f.key);
          // A paused flow is a plain <div>, not a Link — same card, no navigation.
          const Card = off ? "div" : Link;
          const cardProps = off
            ? { "aria-disabled": "true", title: "Temporarily disabled" }
            : { to: flowRoute(f.key) };
          return (
            <Section key={f.key} delay={i * 0.06}>
              <Card {...cardProps} className={cn("card group relative block h-full overflow-hidden p-5", off && "cursor-not-allowed opacity-70")}>
                <div className={cn("pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-gradient-to-br opacity-60 blur-2xl", f.grad)} />
                <div className="relative flex items-center justify-between gap-2">
                  <span className={cn("inline-flex items-center gap-2 rounded-lg px-2.5 py-1 text-sm font-semibold", f.chip)}>
                    <f.icon size={16} /> {f.label}
                  </span>
                  {off ? (
                    <span className="shrink-0 text-[0.625rem] font-semibold lowercase text-rose-500 dark:text-rose-400">temporarily disabled</span>
                  ) : (
                    <ArrowRight size={16} className="text-slate-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-slate-500 dark:text-slate-600" />
                  )}
                </div>
                <div className="relative mt-4 flex items-end justify-between">
                  <div>
                    <p className="font-display text-3xl font-bold text-slate-900 dark:text-white">{f.stats.total}</p>
                    <p className="text-xs text-slate-400">runs</p>
                  </div>
                  <div className="text-right">
                    <p className={cn("font-display text-xl font-bold", f.text)}>{f.stats.passRate}%</p>
                    <p className="text-xs text-slate-400">pass rate</p>
                  </div>
                </div>
                <div className="relative mt-3">
                  <TrendBars data={f.series.data} titles={f.series.titles} height={30} color={f.bar} />
                </div>
                <div className="relative mt-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span className="inline-flex items-center gap-1.5"><Extra size={13} /> {flowExtra[f.key].text}</span>
                  <span className="inline-flex items-center gap-1.5">{f.stats.failed} failed</span>
                </div>
              </Card>
            </Section>
          );
        })}
      </div>

      {/* Activity by flow + side rail */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Section className="lg:col-span-2">
          <div className="card h-full p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Activity by flow</h3>
                <p className="text-xs text-slate-400">your runs per day · {windowNote}</p>
              </div>
              <Link to="/dashboard/runs" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">
                Runs &amp; bugs <ArrowRight size={15} />
              </Link>
            </div>
            <div className="mt-5">
              <GroupedBars labels={activityLabels} titles={activityTitles} series={stackedSeries}
                empty={`No runs in the last ${activityDays} days.`} />
            </div>
          </div>
        </Section>

        <Section delay={0.08}>
          <div className="card flex h-full flex-col gap-4 p-5">
            {/* where the bugs are */}
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
                <Bug size={15} className="text-rose-500" /> Bugs by flow
              </h3>
              <div className="mt-3 space-y-2.5">
                {FLOWS.map((f) => (
                  <div key={f.key} className="flex items-center gap-2.5">
                    <span className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-md", f.chip)}><f.icon size={12} /></span>
                    <span className="w-20 shrink-0 truncate text-xs text-slate-500 dark:text-slate-400">{f.label}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                      <motion.div className={cn("h-full rounded-full", f.bar)} initial={{ width: 0 }}
                        whileInView={{ width: `${Math.round((bugsByFlow[f.key] / maxBugs) * 100)}%` }}
                        viewport={{ once: true }} transition={{ duration: 0.6, ease: "easeOut" }} />
                    </div>
                    <span className="w-6 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-200">{bugsByFlow[f.key]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* plan usage */}
            {usage && (
              <div className="border-t border-slate-100 pt-4 dark:border-white/10">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-white"><Zap size={14} className="text-brand-500" /> Company runs this month</span>
                  <Link to="/dashboard/billing" className="text-xs font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">Plan</Link>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                    <motion.div className={cn("h-full rounded-full", atLimit ? "bg-rose-500" : nearLimit ? "bg-amber-500" : "bg-brand-500")}
                      initial={{ width: 0 }} animate={{ width: `${usage.runs_percent}%` }} transition={{ duration: 0.6, ease: "easeOut" }} />
                  </div>
                  <span className="shrink-0 text-xs text-slate-500 dark:text-slate-400">{usage.runs_this_month} / {usage.runs_limit >= 999999 ? "∞" : usage.runs_limit}</span>
                </div>
              </div>
            )}

            {/* quick jumps to each flow */}
            <div className="mt-auto flex flex-col gap-2 border-t border-slate-100 pt-4 dark:border-white/10">
              <Link to="/dashboard/extension" className="inline-flex items-center justify-between rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5">
                <span className="inline-flex items-center gap-2"><FLOW_BY_KEY.extension.icon size={16} /> Extension</span>
                <ArrowUpRight size={15} className="text-slate-400" />
              </Link>
              <Link to="/dashboard/exe" className="inline-flex items-center justify-between rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5">
                <span className="inline-flex items-center gap-2"><FLOW_BY_KEY.exe.icon size={16} /> Exe Agent</span>
                <ArrowUpRight size={15} className="text-slate-400" />
              </Link>
              {/* Projects is paused (App.jsx hard-blocks /projects). Rendered inert
                  and labelled, to match the sidebar — it used to be styled exactly
                  like the two working tiles above and dropped you on a
                  "temporarily unavailable" page with no warning. */}
              <div
                aria-disabled="true"
                title="Temporarily disabled"
                className="inline-flex cursor-not-allowed items-center justify-between rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-slate-400 opacity-70 dark:border-white/10 dark:text-slate-500"
              >
                <span className="inline-flex items-center gap-2"><FolderKanban size={16} /> Projects</span>
                <span className="text-[0.625rem] font-semibold lowercase text-rose-500 dark:text-rose-400">temporarily disabled</span>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* Latest runs — mixed across flows */}
      <Section className="mt-4">
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Latest runs</h3>
            <Link to="/dashboard/runs" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400">View all <ArrowRight size={15} /></Link>
          </div>
          <div className="mt-3 divide-y divide-slate-100 dark:divide-white/5">
            {loading ? (
              [0, 1, 2, 3, 4].map((i) => <RunRowSkeleton key={i} />)
            ) : recent.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-500/10 text-brand-500"><ListChecks size={20} /></span>
                <p className="text-sm text-slate-500 dark:text-slate-400">No runs yet — generate some test cases, or run one live from the Extension or the Exe Agent.</p>
                {/* /dashboard/new is not a registered route: this, the primary
                    empty-state CTA, 404'd for every brand-new account. */}
                <Button to="/dashboard/generate" size="sm" disabled={atLimit}><Sparkles size={16} /> Generate test cases</Button>
              </div>
            ) : (
              recent.map((r, i) => {
                const f = FLOW_BY_KEY[r.flow];
                return (
                  <motion.div
                    key={`${r.flow}-${r.id}`}
                    initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                    transition={{ delay: i * 0.04, duration: 0.3 }}
                    className="flex items-center gap-3 py-2.5"
                  >
                    <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", f.chip)}><f.icon size={15} /></span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-white">{r.instruction || r.url || "—"}</p>
                      <p className="truncate text-xs text-slate-400">{f.label} · {r.browser || "cloud"} · {relTime(r.createdAt)}</p>
                    </div>
                    <StatusBadge status={r.status} />
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </Section>
    </>
  );
}
